/*
 * Video Testimonials V2 (sections/video-testimonials-v2.liquid)
 *
 * - Plays one video at a time inline, with native controls once started.
 * - Pauses a video when it scrolls out of view.
 * - Shows prev/next arrows only when the list overflows; centers short lists.
 */
(() => {
  class RootmdV2VideoTestimonials extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-video-testimonials-viewport]');
      this.list = this.viewport?.querySelector('ul');
      this.cards = Array.from(this.querySelectorAll('[data-video-testimonials-card]'));
      this.arrows = this.querySelector('[data-video-testimonials-arrows]');
      this.prevButton = this.querySelector('[data-video-testimonials-prev]');
      this.nextButton = this.querySelector('[data-video-testimonials-next]');

      this.updateArrows = this.updateArrows.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        const playButton = card.querySelector('[data-video-testimonials-play]');
        if (!video || !playButton) return;

        playButton.addEventListener('click', () => this.play(card));
        video.addEventListener('ended', () => this.reset(card));
      });

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) this.reset(entry.target, false);
            });
          },
          { threshold: 0.25 }
        );
        this.cards.forEach((card) => this.visibilityObserver.observe(card));
      }

      if (this.viewport) {
        this.prevButton?.addEventListener('click', () => this.scrollByPage(-1));
        this.nextButton?.addEventListener('click', () => this.scrollByPage(1));
        this.viewport.addEventListener('scroll', this.updateArrows, { passive: true });
        window.addEventListener('resize', this.updateArrows);
        this.updateArrows();
      }

      this.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      window.removeEventListener('resize', this.updateArrows);
    }

    play(card) {
      const video = card.querySelector('video');
      if (!video) return;

      this.cards.forEach((otherCard) => {
        if (otherCard !== card) this.reset(otherCard, false);
      });

      card.classList.add('is-playing');
      video.controls = true;
      const playback = video.play();
      if (playback && typeof playback.catch === 'function') {
        playback.catch(() => this.reset(card, false));
      }
      video.focus({ preventScroll: true });
    }

    /* Pause a card's video and bring back its play button. */
    reset(card, rewind = true) {
      const video = card.querySelector('video');
      if (!video || !card.classList.contains('is-playing')) return;

      video.pause();
      video.controls = false;
      if (rewind) video.currentTime = 0;
      card.classList.remove('is-playing');
    }

    updateArrows() {
      const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
      const overflows = scrollWidth > clientWidth + 2;

      if (this.arrows) this.arrows.hidden = !overflows;
      if (this.list) this.list.style.justifyContent = overflows ? '' : 'center';
      if (this.prevButton) this.prevButton.disabled = scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
    }

    scrollByPage(direction) {
      this.viewport.scrollBy({ left: direction * this.viewport.clientWidth * 0.85 });
    }

    onBlockSelect(event) {
      const card = event.target.closest('[data-video-testimonials-card]');
      card?.scrollIntoView({ block: 'nearest', inline: 'start' });
    }
  }

  if (!customElements.get('rootmd-v2-video-testimonials')) {
    customElements.define('rootmd-v2-video-testimonials', RootmdV2VideoTestimonials);
  }
})();
